import type { ISchedule, ScheduleBuffer } from "@gettactic/api";
import { addMinutes, subMinutes } from "date-fns";
import { dateStringToLocalTime } from "../date-time/dateStringToLocalTime";

/**
 * The buffer a reservation holds around itself, resolved into the window the
 * resource is actually blocked for. Reservation times come back as wall clock
 * with a fake `Z`, so they are parsed the way every calendar parses them.
 */
export interface ScheduleBufferWindow {
  /** The reservation's own period. */
  start: Date;
  end: Date;
  /** The period the resource is held for, buffer included. */
  blockedStart: Date;
  blockedEnd: Date;
  beforeMinutes: number;
  afterMinutes: number;
  /** False when the reservation declares no buffer of its own. */
  hasBuffer: boolean;
}

type SchedulePeriod = Pick<ISchedule, "start" | "end"> & {
  buffer?: ScheduleBuffer | null;
};

/** Minutes a reservation blocks on each side, 0 when it declares no buffer. */
export const getScheduleBufferMinutes = (
  buffer?: ScheduleBuffer | null
): { before: number; after: number } => ({
  before: Math.max(0, buffer?.before_minutes ?? 0),
  after: Math.max(0, buffer?.after_minutes ?? 0)
});

export const getScheduleBufferWindow = (
  schedule: SchedulePeriod
): ScheduleBufferWindow => {
  const start = new Date(dateStringToLocalTime(schedule.start));
  const end = new Date(dateStringToLocalTime(schedule.end));
  const { before, after } = getScheduleBufferMinutes(schedule.buffer);

  return {
    start,
    end,
    blockedStart: subMinutes(start, before),
    blockedEnd: addMinutes(end, after),
    beforeMinutes: before,
    afterMinutes: after,
    hasBuffer: before > 0 || after > 0
  };
};

/**
 * A buffer length as the pickers and the blocked-time line word it: "None",
 * "45m", "1h", "1h 15m".
 */
export const formatBufferMinutes = (minutes: number): string => {
  if (minutes <= 0) return "None";

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest}m`;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
};

/**
 * Lengths the buffer pickers offer: quarter hours up to two hours, then whole
 * hours. Core's own ceiling (2880) is far past anything worth listing.
 */
export const BUFFER_MINUTE_OPTIONS = [
  0, 15, 30, 45, 60, 75, 90, 105, 120, 180, 240
];
